/**
 * Weapon-delivery planning: place a target, choose a terminal dive or a
 * ballistic drop, and read the geometry the physics produces — with every
 * number the operator is accountable for.
 *
 * Three things are made unmissable here on purpose, because this is the surface
 * that decides where a warhead lands:
 *
 *   - TIMING SENSITIVITY — how far the hit moves per second of release error.
 *   - WIND CORRECTION — how far the aim was shifted upwind, or, loudly, that
 *     wind was never given and the solution is uncorrected.
 *   - DRAFT ≠ FLY — the "draft as mission" button writes a reviewable route,
 *     exactly like laying waypoints by hand. It does not upload and does not
 *     arm. Uploading is a separate, deliberate action over in the PLAN panel.
 *
 * The dock only ever asks the backend to COMPUTE. Nothing here reaches a
 * vehicle: the solution is drawn on the map and, on request, drafted into the
 * plan, and that is the whole of its authority.
 */

import { useState, type ReactNode } from 'react';

import {
  planDive,
  planRelease,
  suggestDiveAngles,
  type AttackQuality,
  type DiveAngles,
  type DiveRequest,
  type ReleaseRequest,
  type WindConditions,
} from '@/api/attack';
import { useDroneIdentity, usePrimarySelectedId, useDroneScalars } from '@/stores/droneStore';
import { haversineM, missionActions } from '@/stores/missionStore';
import { usePlanDock } from '@/features/planning/planDocks';
import {
  targetingActions,
  useDiveParams,
  useReleaseParams,
  useTargetingMode,
  useTargetingPlacing,
  useTargetingSolution,
  useTargetingTarget,
} from '@/stores/targetingStore';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';
const TARGET_RED = '#ff3b47';

const QUALITY_COLOR: Record<AttackQuality, string> = {
  good: OK,
  acceptable: WARN,
  marginal: DANGER,
};

/** Initial bearing from one point to another, degrees true. Mirrors the wire
 *  boundary's convention; used to default the run-in down the aircraft's line
 *  to the target rather than making the operator read it off the map. */
function bearingDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = Math.PI / 180;
  const y = Math.sin((lon2 - lon1) * toRad) * Math.cos(lat2 * toRad);
  const x =
    Math.cos(lat1 * toRad) * Math.sin(lat2 * toRad) -
    Math.sin(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.cos((lon2 - lon1) * toRad);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

function fmtEnergy(j: number): string {
  if (j >= 1_000_000) return `${(j / 1_000_000).toFixed(1)} MJ`;
  if (j >= 1000) return `${(j / 1000).toFixed(0)} kJ`;
  return `${Math.round(j)} J`;
}

function fmtDist(m: number): string {
  const v = Math.abs(m);
  return v < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(2)} km`;
}

/** Parse an optional numeric field. Blank returns null; garbage returns null. */
function optionalNumber(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function AttackDock(): ReactNode {
  const { open, setOpen } = usePlanDock('attack');

  const editingDrone = usePrimarySelectedId();
  const identity = useDroneIdentity(editingDrone ?? '');
  const scalars = useDroneScalars(editingDrone ?? '', ['lat', 'lon', 'groundSpeed']);

  const target = useTargetingTarget();
  const mode = useTargetingMode();
  const placing = useTargetingPlacing();
  const solution = useTargetingSolution();
  const dive = useDiveParams();
  const release = useReleaseParams();

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; warn: boolean } | null>(null);
  const [advanced, setAdvanced] = useState(false);
  const [angles, setAngles] = useState<DiveAngles | null>(null);

  // Wind at the target. Both blank means "uncorrected" — the solution says so
  // and the map draws no aim offset. This is the difference between a real zero
  // correction and no correction at all.
  const [windSpeed, setWindSpeed] = useState('');
  const [windFrom, setWindFrom] = useState('');

  // Airframe / store / dropper overrides. Blank keeps the backend defaults, so
  // an operator never has to fill a spec sheet to get a solution.
  const [massKg, setMassKg] = useState('');
  const [maxDiveSpeedMs, setMaxDiveSpeedMs] = useState('');
  const [storeMassKg, setStoreMassKg] = useState('');
  const [terminalVelocityMs, setTerminalVelocityMs] = useState('');
  const [servoChannel, setServoChannel] = useState('');
  const [openPwm, setOpenPwm] = useState('');
  const [closedPwm, setClosedPwm] = useState('');

  const note = (text: string, warn = false) => setMessage({ text, warn });

  // The auto run-in bearing: down the aircraft's line to the target. Falls back
  // to due north only when there is no aircraft position to measure from.
  const autoBearing =
    target && typeof scalars.lat === 'number' && typeof scalars.lon === 'number'
      ? bearingDeg(scalars.lat, scalars.lon, target.lat, target.lon)
      : 0;
  const effectiveBearing = dive.approachBearingDeg ?? autoBearing;

  const windConditions = (): WindConditions | null => {
    const speed = optionalNumber(windSpeed);
    const from = optionalNumber(windFrom);
    if (speed === null || from === null) return null;
    return { windSpeedMs: speed, windFromDeg: from };
  };

  const airframeOverride = () => {
    const overrides: Record<string, number> = {};
    const mass = optionalNumber(massKg);
    const maxDive = optionalNumber(maxDiveSpeedMs);
    if (mass !== null) overrides.massKg = mass;
    if (maxDive !== null) overrides.maxDiveSpeedMs = maxDive;
    return Object.keys(overrides).length ? overrides : undefined;
  };

  const runDive = async () => {
    if (!target) return;
    setBusy(true);
    setMessage(null);
    const conditions = windConditions();
    const body: DiveRequest = {
      targetLat: target.lat,
      targetLon: target.lon,
      pushoverAltM: dive.pushoverAltM,
      diveAngleDeg: dive.diveAngleDeg,
      approachBearingDeg: effectiveBearing,
      ...(airframeOverride() ? { airframe: airframeOverride() } : {}),
      conditions,
    };
    const result = await planDive(body);
    setBusy(false);
    if (!result.ok) {
      note(result.error.message, true);
      return;
    }
    targetingActions.setSolution({ kind: 'dive', windKnown: conditions !== null, data: result.data });
    note(`Dive solution — ${result.data.quality}`);
  };

  const runRelease = async () => {
    if (!target) return;
    setBusy(true);
    setMessage(null);
    const conditions = windConditions();
    const storeOverride = () => {
      const overrides: Record<string, number> = {};
      const mass = optionalNumber(storeMassKg);
      const vt = optionalNumber(terminalVelocityMs);
      if (mass !== null) overrides.massKg = mass;
      if (vt !== null) overrides.terminalVelocityMs = vt;
      return Object.keys(overrides).length ? overrides : undefined;
    };
    const dropperOverride = () => {
      const ch = optionalNumber(servoChannel);
      const op = optionalNumber(openPwm);
      const cl = optionalNumber(closedPwm);
      if (ch === null || op === null || cl === null) return undefined;
      return { servoChannel: ch, openPwm: op, closedPwm: cl };
    };
    const body: ReleaseRequest = {
      targetLat: target.lat,
      targetLon: target.lon,
      releaseAltM: release.releaseAltM,
      groundSpeedMs: release.groundSpeedMs,
      trackBearingDeg: effectiveBearing,
      ...(storeOverride() ? { store: storeOverride() } : {}),
      ...(dropperOverride() ? { dropper: dropperOverride() } : {}),
      conditions,
    };
    const result = await planRelease(body);
    setBusy(false);
    if (!result.ok) {
      note(result.error.message, true);
      return;
    }
    targetingActions.setSolution({
      kind: 'release',
      windKnown: conditions !== null,
      data: result.data,
    });
    note(`Release solution — ${result.data.quality}`);
  };

  const suggest = async () => {
    if (!target) {
      note('Place a target first', true);
      return;
    }
    // Ground range for the angle suggestion is the aircraft's horizontal
    // distance to the target when we have a position, else a nominal 1 km.
    const groundRangeM =
      typeof scalars.lat === 'number' && typeof scalars.lon === 'number'
        ? haversineM(scalars.lat, scalars.lon, target.lat, target.lon)
        : 1000;
    const result = await suggestDiveAngles({ pushoverAltM: dive.pushoverAltM, groundRangeM });
    if (!result.ok) {
      note(result.error.message, true);
      return;
    }
    setAngles(result.data);
  };

  // DRAFT ≠ FLY. This writes the returned waypoints into the plan's draft for
  // review — identical to laying them by hand. It never uploads and never arms;
  // the aircraft only ever flies what the operator later uploads from PLAN.
  const draftAsMission = () => {
    if (!editingDrone || !solution) return;
    missionActions.setDraft(editingDrone, solution.data.mission.map((wp) => ({ ...wp })));
    note(
      `Drafted ${solution.data.mission.length} waypoints into ${editingDrone} — review and upload from PLAN. Not uploaded, not flown.`,
    );
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open the targeting planner"
        className="plan-attack-pill glass-pill pointer-events-auto absolute left-[calc(var(--plan-l-w)+24px)] top-[86px] z-10 rounded-full px-3 py-2 font-mono text-[9px] font-bold tracking-wide"
        style={{ color: target ? TARGET_RED : 'rgba(244,244,245,0.7)' }}
      >
        TARGETING{solution ? ' · 1 SOLUTION' : target ? ' · TARGET SET' : ''}
      </button>
    );
  }

  return (
    <aside
      aria-label="Targeting planner"
      className="attack-dock glass scroll-thin pointer-events-auto absolute left-[calc(var(--plan-l-w)+24px)] top-[86px] bottom-[244px] z-10 flex w-[var(--plan-dock-w)] flex-col overflow-hidden rounded-[20px]"
    >
      <div className="flex flex-none items-center gap-2 px-3.5 pt-3 pb-2">
        <span className="label-mono text-[10px]" style={{ color: TARGET_RED }}>
          TARGETING
        </span>
        <span className="label-mono text-[8px] text-white/40">PLANS · NEVER FIRES</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close the targeting planner"
          className="ml-auto label-mono text-[8px] text-white/45 hover:text-white"
        >
          HIDE
        </button>
      </div>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-3.5 pb-2">
        {/* Target placement, mirroring how a zone captures a map click. */}
        <div className="mb-3 flex flex-col gap-1.5">
          <div className="label-mono text-[9px]">TARGET</div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => (placing ? targetingActions.cancelPlacement() : targetingActions.armPlacement())}
              aria-label={placing ? 'Cancel placing the target' : 'Place the target on the map'}
              aria-pressed={placing}
              className="rounded-full border px-3 py-1.5 font-mono text-[9px] font-bold tracking-wide"
              style={{
                color: placing ? '#0c0c0d' : TARGET_RED,
                background: placing ? TARGET_RED : 'transparent',
                borderColor: `color-mix(in oklch, ${TARGET_RED} 55%, transparent)`,
              }}
            >
              {placing ? 'CLICK THE MAP…' : target ? 'MOVE TARGET' : 'PLACE TARGET'}
            </button>
            {target ? (
              <button
                type="button"
                onClick={() => {
                  targetingActions.clearTarget();
                  setAngles(null);
                }}
                aria-label="Clear the target"
                className="rounded-full border border-white/15 px-2.5 py-1.5 font-mono text-[9px] font-bold text-white/55 hover:text-white"
              >
                CLEAR
              </button>
            ) : null}
            <span className="tnum ml-auto font-mono text-[10px] text-white/55">
              {target ? `${target.lat.toFixed(5)}, ${target.lon.toFixed(5)}` : 'none'}
            </span>
          </div>
          {placing ? (
            <p className="text-[9.5px] leading-relaxed text-white/45">
              Click the map to drop the target. Placing captures the click, so no waypoint is added.
            </p>
          ) : null}
        </div>

        {/* Dive / drop mode. */}
        <div className="well mb-3 flex gap-1 rounded-[13px] p-1">
          {(['dive', 'release'] as const).map((m) => {
            const active = mode === m;
            return (
              <button
                key={m}
                type="button"
                onClick={() => targetingActions.setMode(m)}
                aria-pressed={active}
                className="flex-1 rounded-[10px] py-[7px] font-mono text-[10px] font-bold tracking-wide transition-colors"
                style={{
                  background: active ? 'rgba(255,255,255,0.92)' : 'transparent',
                  color: active ? '#0c0c0d' : 'rgba(244,244,245,0.6)',
                }}
              >
                {m === 'dive' ? 'DIVE' : 'DROP'}
              </button>
            );
          })}
        </div>

        {mode === 'dive' ? (
          <div className="mb-3 flex flex-col gap-2">
            <div className="flex items-center gap-1.5">
              <NumberField
                label="dive angle"
                suffix="°"
                value={dive.diveAngleDeg}
                onChange={(n) => targetingActions.setDive({ diveAngleDeg: n })}
                ariaLabel="Dive angle in degrees"
              />
              <button
                type="button"
                onClick={() => void suggest()}
                aria-label="Suggest dive angles"
                className="rounded-full border border-white/15 px-2.5 py-1 font-mono text-[9px] font-bold text-white/70 hover:text-white"
              >
                SUGGEST
              </button>
            </div>
            {angles ? (
              <div className="flex flex-wrap gap-1.5">
                {(
                  [
                    ['shallow', angles.shallow, 'gentle · long slant'],
                    ['optimal', angles.optimal, 'best tradeoff'],
                    ['steep', angles.steep, 'near-vertical terminal'],
                  ] as Array<[string, number, string]>
                ).map(([name, value, meaning]) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => targetingActions.setDive({ diveAngleDeg: Math.round(value) })}
                    aria-label={`Use ${name} dive angle, ${Math.round(value)} degrees — ${meaning}`}
                    title={meaning}
                    className="flex flex-col rounded-[10px] border border-white/12 bg-white/5 px-2.5 py-1 text-left hover:bg-white/10"
                  >
                    <span className="font-mono text-[10px] font-bold text-white/85">
                      {name.toUpperCase()} {Math.round(value)}°
                    </span>
                    <span className="font-mono text-[8px] text-white/45">{meaning}</span>
                  </button>
                ))}
              </div>
            ) : null}
            <NumberField
              label="pushover alt"
              suffix="m"
              value={dive.pushoverAltM}
              onChange={(n) => targetingActions.setDive({ pushoverAltM: n })}
              ariaLabel="Pushover altitude in metres"
            />
            <div className="flex items-center gap-1.5">
              <NumberField
                label="approach brg"
                suffix="°"
                value={Math.round(effectiveBearing)}
                onChange={(n) => targetingActions.setDive({ approachBearingDeg: n })}
                ariaLabel="Approach bearing in degrees"
              />
              <span className="font-mono text-[8.5px] text-white/40">
                {dive.approachBearingDeg === null ? 'auto — aircraft to target' : 'manual'}
              </span>
              {dive.approachBearingDeg !== null ? (
                <button
                  type="button"
                  onClick={() => targetingActions.setDive({ approachBearingDeg: null })}
                  aria-label="Reset approach bearing to automatic"
                  className="rounded-full border border-white/15 px-2 py-0.5 font-mono text-[8px] font-bold text-white/55 hover:text-white"
                >
                  AUTO
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="mb-3 flex flex-col gap-2">
            <NumberField
              label="release alt"
              suffix="m"
              value={release.releaseAltM}
              onChange={(n) => targetingActions.setRelease({ releaseAltM: n })}
              ariaLabel="Release altitude in metres"
            />
            <div className="flex items-center gap-1.5">
              <NumberField
                label="ground speed"
                suffix="m/s"
                value={release.groundSpeedMs}
                onChange={(n) => targetingActions.setRelease({ groundSpeedMs: n })}
                ariaLabel="Ground speed in metres per second"
              />
              {typeof scalars.groundSpeed === 'number' ? (
                <button
                  type="button"
                  onClick={() =>
                    targetingActions.setRelease({
                      groundSpeedMs: Math.round((scalars.groundSpeed ?? 0) * 10) / 10,
                    })
                  }
                  aria-label="Use the aircraft's current ground speed"
                  className="rounded-full border border-white/15 px-2 py-0.5 font-mono text-[8px] font-bold text-white/55 hover:text-white"
                >
                  LIVE {(scalars.groundSpeed ?? 0).toFixed(0)}
                </button>
              ) : null}
            </div>
            <div className="flex items-center gap-1.5">
              <NumberField
                label="track brg"
                suffix="°"
                value={Math.round(effectiveBearing)}
                onChange={(n) => targetingActions.setDive({ approachBearingDeg: n })}
                ariaLabel="Track bearing in degrees"
              />
              <span className="font-mono text-[8.5px] text-white/40">
                {dive.approachBearingDeg === null ? 'auto — aircraft to target' : 'manual'}
              </span>
            </div>
          </div>
        )}

        {/* Wind — the difference between a corrected and an uncorrected solution. */}
        <div className="mb-3 flex flex-col gap-1.5">
          <div className="label-mono text-[9px]">WIND AT TARGET (optional)</div>
          <div className="flex items-center gap-1.5">
            <NumberFieldText
              label="speed"
              suffix="m/s"
              value={windSpeed}
              onChange={setWindSpeed}
              ariaLabel="Wind speed in metres per second"
            />
            <NumberFieldText
              label="from"
              suffix="°"
              value={windFrom}
              onChange={setWindFrom}
              ariaLabel="Wind direction it blows from, in degrees"
            />
          </div>
          <p className="text-[8.5px] leading-relaxed text-white/40">
            Leave blank to plan uncorrected — the solution will say so, loudly.
          </p>
        </div>

        {/* Advanced, per-airframe / per-store overrides. */}
        <button
          type="button"
          onClick={() => setAdvanced((v) => !v)}
          aria-expanded={advanced}
          className="mb-2 flex w-full items-center gap-2 label-mono text-[9px] text-white/55 hover:text-white"
        >
          <span>{advanced ? '▾' : '▸'}</span>
          ADVANCED — AIRFRAME &amp; STORE
        </button>
        {advanced ? (
          <div className="mb-3 flex flex-col gap-2 rounded-[12px] border border-white/8 bg-black/20 p-2.5">
            {mode === 'dive' ? (
              <div className="flex items-center gap-1.5">
                <NumberFieldText label="mass" suffix="kg" value={massKg} onChange={setMassKg} ariaLabel="Airframe mass in kilograms" />
                <NumberFieldText
                  label="max dive"
                  suffix="m/s"
                  value={maxDiveSpeedMs}
                  onChange={setMaxDiveSpeedMs}
                  ariaLabel="Maximum dive speed in metres per second"
                />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1.5">
                  <NumberFieldText label="store mass" suffix="kg" value={storeMassKg} onChange={setStoreMassKg} ariaLabel="Store mass in kilograms" />
                  <NumberFieldText
                    label="term vel"
                    suffix="m/s"
                    value={terminalVelocityMs}
                    onChange={setTerminalVelocityMs}
                    ariaLabel="Store terminal velocity in metres per second"
                  />
                </div>
                <div className="label-mono text-[8px] text-white/40">DROPPER — PER-VEHICLE WIRING</div>
                <div className="flex items-center gap-1.5">
                  <NumberFieldText label="ch" value={servoChannel} onChange={setServoChannel} ariaLabel="Dropper servo channel" />
                  <NumberFieldText label="open" value={openPwm} onChange={setOpenPwm} ariaLabel="Dropper open PWM" />
                  <NumberFieldText label="closed" value={closedPwm} onChange={setClosedPwm} ariaLabel="Dropper closed PWM" />
                </div>
              </>
            )}
          </div>
        ) : null}

        <button
          type="button"
          disabled={busy || !target}
          onClick={() => void (mode === 'dive' ? runDive() : runRelease())}
          aria-label="Compute the attack solution"
          className="mb-3 w-full rounded-[12px] border px-3 py-2 font-mono text-[10px] font-bold tracking-wide disabled:opacity-40"
          style={{ color: '#0c0c0d', background: TARGET_RED, borderColor: TARGET_RED }}
        >
          {busy ? 'SOLVING…' : !target ? 'PLACE A TARGET FIRST' : mode === 'dive' ? 'COMPUTE DIVE' : 'COMPUTE RELEASE'}
        </button>

        {solution ? <Solution solution={solution} onDraft={draftAsMission} canDraft={editingDrone !== null} /> : null}

        {message ? (
          <p className="mt-2 font-mono text-[10px] leading-relaxed" style={{ color: message.warn ? WARN : OK }}>
            {message.text}
          </p>
        ) : null}
      </div>

      <p className="flex-none border-t border-white/10 px-3.5 py-2.5 text-[9px] leading-relaxed text-white/40">
        {identity ? (
          <>
            Planning for <span className="font-mono text-white/70">{identity.callsign}</span>.{' '}
          </>
        ) : (
          <>Pick an aircraft in PLAN to draft for. </>
        )}
        <span style={{ color: OK }}>DRAFT ≠ FLY — nothing here uploads or arms.</span>
      </p>
    </aside>
  );
}

/** The solution readout. Energy is the headline; timing and wind are the two
 *  numbers this whole surface exists to keep in front of the operator. */
function Solution({
  solution,
  onDraft,
  canDraft,
}: {
  solution: NonNullable<ReturnType<typeof useTargetingSolution>>;
  onDraft: () => void;
  canDraft: boolean;
}): ReactNode {
  const d = solution.data;
  const timeS = solution.kind === 'dive' ? solution.data.timeToImpactS : solution.data.fallTimeS;
  const timeLabel = solution.kind === 'dive' ? 'TIME TO IMPACT' : 'FALL TIME';

  return (
    <div className="flex flex-col gap-2.5 rounded-[14px] border border-white/10 bg-black/25 p-3">
      <div className="flex items-center gap-2">
        <span className="label-mono text-[9px]">SOLUTION</span>
        <span
          className="rounded-full px-2 py-[2px] font-mono text-[8px] font-bold tracking-wide"
          style={{
            background: `color-mix(in oklch, ${QUALITY_COLOR[d.quality]} 22%, transparent)`,
            color: QUALITY_COLOR[d.quality],
          }}
        >
          {d.quality.toUpperCase()}
        </span>
      </div>

      {/* Impact energy — the headline effect number. */}
      <div className="rounded-[12px] border border-white/10 bg-white/5 px-3 py-2.5">
        <div className="label-mono text-[8.5px]">IMPACT ENERGY</div>
        <div className="tnum mt-0.5 font-mono text-[22px] font-bold" style={{ color: TARGET_RED }}>
          {fmtEnergy(d.impactEnergyJ)}
        </div>
        <div className="font-mono text-[9.5px] text-white/50">
          at {Math.round(d.impactSpeedMs)} m/s impact
        </div>
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        <Stat k={timeLabel} v={`${timeS.toFixed(1)} s`} />
        {solution.kind === 'dive' ? (
          <>
            <Stat k="SLANT RANGE" v={fmtDist(solution.data.slantRangeM)} />
            <Stat k="GROUND RANGE" v={fmtDist(solution.data.groundRangeM)} />
            <Stat k="HEIGHT DROP" v={`${Math.round(solution.data.heightDropM)} m`} />
          </>
        ) : (
          <>
            <Stat k="RELEASE LEAD" v={fmtDist(solution.data.releaseLeadM)} />
            <Stat k="FORWARD THROW" v={fmtDist(solution.data.forwardThrowM)} />
            <Stat k="RUN BEARING" v={`${String(Math.round(solution.data.runBearingDeg)).padStart(3, '0')}°`} />
          </>
        )}
      </div>

      {/* Timing sensitivity — how far a one-second error walks the hit. */}
      <div
        className="rounded-[12px] border px-3 py-2"
        style={{
          borderColor: `color-mix(in oklch, ${WARN} 45%, transparent)`,
          background: `color-mix(in oklch, ${WARN} 8%, transparent)`,
        }}
      >
        <div className="label-mono text-[8.5px]" style={{ color: WARN }}>
          TIMING SENSITIVITY
        </div>
        <div className="mt-0.5 font-mono text-[11px] leading-snug text-white/80">
          A 1 s release error moves the hit{' '}
          <span className="tnum font-bold" style={{ color: WARN }}>
            {fmtDist(d.timingSensitivityM)}
          </span>
          .
        </div>
      </div>

      {/* Wind correction — corrected upwind, or loudly uncorrected. */}
      <div
        className="rounded-[12px] border px-3 py-2"
        style={{
          borderColor: solution.windKnown
            ? 'rgba(255,255,255,0.12)'
            : `color-mix(in oklch, ${DANGER} 45%, transparent)`,
          background: solution.windKnown ? 'rgba(255,255,255,0.04)' : `color-mix(in oklch, ${DANGER} 9%, transparent)`,
        }}
      >
        <div className="label-mono text-[8.5px]" style={{ color: solution.windKnown ? undefined : DANGER }}>
          WIND CORRECTION
        </div>
        <div className="mt-0.5 font-mono text-[11px] leading-snug text-white/80">
          {solution.windKnown ? (
            <>
              Aim shifted{' '}
              <span className="tnum font-bold" style={{ color: OK }}>
                {fmtDist(d.windCorrectionM)}
              </span>{' '}
              upwind.
            </>
          ) : (
            <span style={{ color: DANGER }}>Wind unknown — solution is uncorrected.</span>
          )}
        </div>
      </div>

      {d.notes.length ? (
        <div className="flex flex-col gap-1">
          <div className="label-mono text-[8px] text-white/40">ADVISORIES</div>
          {d.notes.map((n, i) => (
            <div key={i} className="flex gap-1.5 text-[10px] leading-relaxed text-white/70">
              <span className="text-white/35">·</span>
              <span>{n}</span>
            </div>
          ))}
        </div>
      ) : null}

      <button
        type="button"
        disabled={!canDraft}
        onClick={onDraft}
        aria-label="Draft this attack as a mission for review"
        title="Writes the waypoints into the plan for review. Does not upload or fly."
        className="w-full rounded-[12px] border px-3 py-2 font-mono text-[10px] font-bold tracking-wide disabled:opacity-40"
        style={{ color: '#0c0c0d', background: OK, borderColor: OK }}
      >
        DRAFT AS MISSION
      </button>
      <p className="text-[8.5px] leading-relaxed text-white/45">
        {canDraft
          ? 'Drafts a reviewable route into PLAN — exactly like laying waypoints by hand. It does not upload and does not fly.'
          : 'Pick an aircraft in PLAN before drafting.'}
      </p>
    </div>
  );
}

function Stat({ k, v }: { k: string; v: string }): ReactNode {
  return (
    <div className="rounded-[10px] border border-white/8 bg-white/5 px-2.5 py-1.5">
      <div className="label-mono text-[8px]">{k}</div>
      <div className="tnum mt-0.5 font-mono text-[12px] text-white/85">{v}</div>
    </div>
  );
}

/** A numeric parameter bound straight to the store, so the map and the card
 *  read the same value. Empty input is treated as zero — every field here
 *  always carries a number. */
function NumberField({
  label,
  suffix,
  value,
  onChange,
  ariaLabel,
}: {
  label: string;
  suffix?: string;
  value: number;
  onChange: (value: number) => void;
  ariaLabel: string;
}): ReactNode {
  return (
    <label className="flex flex-1 items-center gap-1.5 font-mono text-[10px] text-white/50">
      <span className="min-w-[64px]">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(event) => {
          const n = Number(event.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
        aria-label={ariaLabel}
        className="well w-full min-w-0 flex-1 rounded-[8px] px-2 py-1 text-right text-[11px] text-white/90 outline-none"
      />
      {suffix ? <span className="w-6">{suffix}</span> : null}
    </label>
  );
}

/** The string-backed variant, for optional fields where blank means "unset". */
function NumberFieldText({
  label,
  suffix,
  value,
  onChange,
  ariaLabel,
}: {
  label: string;
  suffix?: string;
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
}): ReactNode {
  return (
    <label className="flex flex-1 items-center gap-1.5 font-mono text-[10px] text-white/50">
      <span className="min-w-[42px]">{label}</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        inputMode="decimal"
        aria-label={ariaLabel}
        className="well w-full min-w-0 flex-1 rounded-[8px] px-2 py-1 text-right text-[11px] text-white/90 outline-none"
      />
      {suffix ? <span className="w-6">{suffix}</span> : null}
    </label>
  );
}

export default AttackDock;
